<!-- components/AuthModal.vue -->
<template>
  <Teleport to="body">
    <div class="auth-overlay" @click.self="closeModal">
      <div class="auth-modal" :class="{ 'slide-up': isVisible }">
        <!-- Close Button -->
        <button class="close-btn" @click="closeModal">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <!-- Modal Content -->
        <div class="modal-content">
          <!-- Logo -->
          <div class="auth-header">
            <h2 class="auth-title">My<span>Harf</span></h2>
            <p class="auth-subtitle">
              {{
                isSignUp
                  ? "Create your account and start learning"
                  : "Welcome back! Sign in to continue"
              }}
            </p>
          </div>

          <!-- Auth Mode Toggle -->
          <div class="auth-tabs">
            <button
              class="tab"
              :class="{ active: !isSignUp }"
              @click="switchMode('signin')"
            >
              Sign In
            </button>
            <button
              class="tab"
              :class="{ active: isSignUp }"
              @click="switchMode('signup')"
            >
              Sign Up
            </button>
          </div>

          <!-- Sign In Form -->
          <form
            v-if="!isSignUp"
            @submit.prevent="handleSignIn"
            class="auth-form"
          >
            <div class="form-group">
              <label for="signin-email">Email Address</label>
              <div class="input-wrapper">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                  />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                <input
                  type="email"
                  id="signin-email"
                  v-model="signInForm.email"
                  placeholder="you@example.com"
                  required
                  autocomplete="email"
                />
              </div>
            </div>

            <div class="form-group">
              <label for="signin-password">Password</label>
              <div class="input-wrapper">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                <input
                  :type="showPassword ? 'text' : 'password'"
                  id="signin-password"
                  v-model="signInForm.password"
                  placeholder="Enter your password"
                  required
                  autocomplete="current-password"
                />
                <button
                  type="button"
                  class="toggle-password"
                  @click="showPassword = !showPassword"
                >
                  <svg
                    v-if="!showPassword"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                  <svg
                    v-else
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"
                    />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                </button>
              </div>
            </div>

            <div class="form-options">
              <label class="checkbox-label">
                <input type="checkbox" v-model="rememberMe" />
                <span class="checkmark"></span>
                Remember me
              </label>
              <button
                type="button"
                class="forgot-password"
                @click="showResetPassword = true"
              >
                Forgot password?
              </button>
            </div>

            <button type="submit" class="submit-btn" :disabled="loading">
              <span v-if="loading" class="spinner"></span>
              <span v-else>Sign In</span>
            </button>

            <div v-if="authError" class="error-message">{{ authError }}</div>
          </form>

          <!-- Sign Up Form -->
          <form
            v-if="isSignUp"
            @submit.prevent="handleSignUp"
            class="auth-form"
          >
            <div class="form-group">
              <label for="signup-name">Full Name</label>
              <div class="input-wrapper">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                <input
                  type="text"
                  id="signup-name"
                  v-model="signUpForm.displayName"
                  placeholder="Ahmed Ali"
                  required
                  autocomplete="name"
                />
              </div>
            </div>

            <div class="form-group">
              <label for="signup-email">Email Address</label>
              <div class="input-wrapper">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                  />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                <input
                  type="email"
                  id="signup-email"
                  v-model="signUpForm.email"
                  placeholder="you@example.com"
                  required
                  autocomplete="email"
                />
              </div>
            </div>

            <div class="form-group">
              <label for="signup-password">Password</label>
              <div class="input-wrapper">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                <input
                  :type="showPassword ? 'text' : 'password'"
                  id="signup-password"
                  v-model="signUpForm.password"
                  placeholder="Min 8 characters"
                  required
                  minlength="8"
                  autocomplete="new-password"
                />
                <button
                  type="button"
                  class="toggle-password"
                  @click="showPassword = !showPassword"
                >
                  <svg
                    v-if="!showPassword"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                  <svg
                    v-else
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"
                    />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                </button>
              </div>
              <div
                class="password-hint"
                v-if="signUpForm.password && signUpForm.password.length < 8"
              >
                <span>Password must be at least 8 characters</span>
              </div>
            </div>

            <div class="form-group">
              <label for="signup-role">I want to...</label>
              <div class="input-wrapper">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5" />
                  <path d="M2 12l10 5 10-5" />
                </svg>
                <select id="signup-role" v-model="signUpForm.role" required>
                  <option value="learner">Learn new skills</option>
                  <option value="creator">Create and sell courses</option>
                  <option value="both">Both (Learn & Create)</option>
                </select>
              </div>
            </div>

            <div class="form-options">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  v-model="signUpForm.agreeToTerms"
                  required
                />
                <span class="checkmark"></span>
                I agree to the
                <a href="#" @click.prevent>Terms of Service</a> and
                <a href="#" @click.prevent>Privacy Policy</a>
              </label>
            </div>

            <button type="submit" class="submit-btn" :disabled="loading">
              <span v-if="loading" class="spinner"></span>
              <span v-else>Create Account</span>
            </button>

            <div v-if="authError" class="error-message">{{ authError }}</div>
          </form>

          <!-- Password Reset -->
          <div v-if="showResetPassword" class="reset-password">
            <h3>Reset Password</h3>
            <p>
              Enter your email and we'll send you a link to reset your password.
            </p>
            <div class="form-group">
              <div class="input-wrapper">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                  />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                <input
                  type="email"
                  v-model="resetEmail"
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>
            <div class="reset-actions">
              <button
                type="button"
                class="btn-secondary"
                @click="showResetPassword = false"
              >
                Cancel
              </button>
              <button
                type="button"
                class="submit-btn"
                @click="handleResetPassword"
                :disabled="resetLoading"
              >
                <span v-if="resetLoading" class="spinner"></span>
                <span v-else>Send Reset Link</span>
              </button>
            </div>
            <div v-if="resetMessage" class="success-message">
              {{ resetMessage }}
            </div>
          </div>
          <!-- components/AuthModal.vue - Update the social section -->
          <div class="social-auth" v-if="!showResetPassword">
            <div class="divider">
              <span>or continue with</span>
            </div>
            <button
              type="button"
              class="social-btn google"
              @click="handleGoogleSignIn"
              :disabled="loading"
            >
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path
                  fill="#EA4335"
                  d="M5.26620003,9.76452941 C6.19878754,6.93863203 8.85444915,4.71266667 12,4.71266667 C13.6908333,4.71266667 15.2533333,5.298 16.5086667,6.304 C16.676,6.44533333 16.92,6.438 17.0853333,6.29066667 L19.5973333,3.95066667 C19.792,3.77533333 19.792,3.468 19.5893333,3.302 C17.422,1.48266667 14.762,0.38 12,0.38 C7.732,0.38 4.046,2.68533333 2.166,6.01733333 L5.26620003,9.76452941 Z"
                />
                <path
                  fill="#34A853"
                  d="M5.26620003,14.2354706 L2.166,17.9826667 C4.046,21.3146667 7.732,23.62 12,23.62 C14.656,23.62 17.204,22.5693333 19.3306667,20.862 C19.532,20.704 19.5453333,20.408 19.358,20.2353333 L16.8706667,17.9173333 C16.706,17.768 16.454,17.762 16.286,17.902 C15.058,18.9406667 13.564,19.5106667 12,19.5106667 C8.80333333,19.5106667 6.118,17.2206667 5.26620003,14.2354706 Z"
                />
                <path
                  fill="#4A90E2"
                  d="M20.5133333,11.256 C20.5133333,10.6606667 20.458,10.184 20.354,9.708 L12,9.708 C11.584,9.708 11.24,10.052 11.24,10.468 L11.24,13.292 C11.24,13.708 11.584,14.052 12,14.052 L16.456,14.052 C16.1906667,15.3706667 15.5013333,16.276 14.5733333,16.922 L14.574,16.9213333 L17.062,19.2393333 C17.246,19.406 17.53,19.392 17.704,19.2106667 C19.584,17.112 20.5133333,14.452 20.5133333,11.256 Z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.26620003,9.76452941 L2.166,6.01733333 C1.74,5.304 1.46533333,4.506 1.46533333,3.62 C1.46533333,2.73466667 1.74,1.936 2.166,1.22266667 L5.26620003,4.96952941 C4.746,5.800 4.46066667,6.768 4.46066667,7.866 C4.46066667,8.964 4.746,9.932 5.26620003,10.7645294 L5.26620003,9.76452941 Z"
                />
              </svg>
              Google
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, onUnmounted } from "vue";
import { useAuthStore } from "@/stores/auth";

const emit = defineEmits(["close"]);
const authStore = useAuthStore();

// State
const isVisible = ref(false);
const isSignUp = ref(false);
const showPassword = ref(false);
const rememberMe = ref(false);
const showResetPassword = ref(false);
const resetEmail = ref("");
const resetLoading = ref(false);
const resetMessage = ref("");
const loading = ref(false);
const authError = ref("");

// Forms
const signInForm = reactive({
  email: "",
  password: "",
});

const signUpForm = reactive({
  displayName: "",
  email: "",
  password: "",
  role: "learner",
  agreeToTerms: false,
});

// Methods
const switchMode = (mode) => {
  isSignUp.value = mode === "signup";
  authError.value = "";
  showResetPassword.value = false;
};

const closeModal = () => {
  isVisible.value = false;
  setTimeout(() => {
    emit("close");
    // Reset forms
    Object.assign(signInForm, { email: "", password: "" });
    Object.assign(signUpForm, {
      displayName: "",
      email: "",
      password: "",
      role: "learner",
      agreeToTerms: false,
    });
    authError.value = "";
    showResetPassword.value = false;
    resetEmail.value = "";
    resetMessage.value = "";
  }, 300);
};

const handleSignIn = async () => {
  loading.value = true;
  authError.value = "";

  const result = await authStore.signIn(signInForm.email, signInForm.password);

  if (result.success) {
    closeModal();
  } else {
    authError.value =
      result.error || "Sign in failed. Please check your credentials.";
  }

  loading.value = false;
};

const handleSignUp = async () => {
  // Validate password length
  if (signUpForm.password.length < 8) {
    authError.value = "Password must be at least 8 characters long.";
    return;
  }

  if (!signUpForm.agreeToTerms) {
    authError.value =
      "Please agree to the Terms of Service and Privacy Policy.";
    return;
  }

  loading.value = true;
  authError.value = "";

  const result = await authStore.signUp(
    signUpForm.email,
    signUpForm.password,
    signUpForm.displayName,
  );

  if (result.success) {
    // Success - user created
    closeModal();
    // Show a success message or redirect
    alert(
      "Account created successfully! Please check your email to verify your account.",
    );
  } else {
    authError.value = result.error || "Sign up failed. Please try again.";
  }

  loading.value = false;
};

// Update Google sign-in to actually work
const handleGoogleSignIn = async () => {
  loading.value = true;
  authError.value = "";

  try {
    const result = await authStore.signInWithGoogle();
    if (result.success) {
      closeModal();
    } else {
      authError.value =
        result.error || "Google sign-in failed. Please try again.";
    }
  } catch (error) {
    authError.value = "Google sign-in failed. Please try again.";
    console.error("Google sign-in error:", error);
  } finally {
    loading.value = false;
  }
};

const handleResetPassword = async () => {
  if (!resetEmail.value) {
    authError.value = "Please enter your email address.";
    return;
  }

  resetLoading.value = true;
  authError.value = "";

  const result = await authStore.resetPassword(resetEmail.value);

  if (result.success) {
    resetMessage.value = "Password reset link sent! Check your email.";
    setTimeout(() => {
      showResetPassword.value = false;
      resetMessage.value = "";
    }, 3000);
  } else {
    authError.value =
      result.error || "Failed to send reset link. Please try again.";
  }

  resetLoading.value = false;
};

// Keyboard shortcuts
const handleEscape = (event) => {
  if (event.key === "Escape") {
    closeModal();
  }
};

// Lifecycle
onMounted(() => {
  // Trigger animation
  setTimeout(() => {
    isVisible.value = true;
  }, 10);

  document.addEventListener("keydown", handleEscape);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleEscape);
});

// Watch for error clearing
watch([() => signInForm.email, () => signInForm.password], () => {
  authError.value = "";
});

watch(
  [
    () => signUpForm.email,
    () => signUpForm.password,
    () => signUpForm.displayName,
  ],
  () => {
    authError.value = "";
  },
);
</script>

<style lang="scss" scoped>
$primary-orange: #ff8000;
$primary-blue: #001829;
$hover-orange: #cc5800;

.auth-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.3s ease;
}

.auth-modal {
  position: relative;
  width: 100%;
  max-width: 460px;
  max-height: 90vh;
  background: #fff;
  border-radius: 20px;
  padding: 40px 32px 32px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  transform: translateY(20px);
  opacity: 0;
  transition: all 0.3s ease;
  overflow-y: auto;

  &.slide-up {
    transform: translateY(0);
    opacity: 1;
  }

  @media (max-width: 480px) {
    padding: 32px 20px 24px;
    margin: 16px;
    max-height: 95vh;
  }
}

.close-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  background: transparent;
  border: none;
  padding: 8px;
  cursor: pointer;
  border-radius: 50%;
  transition: all 0.2s ease;
  color: #666;

  &:hover {
    background: #f0f0f0;
    transform: rotate(90deg);
  }

  svg {
    width: 24px;
    height: 24px;
  }
}

.modal-content {
  .auth-header {
    text-align: center;
    margin-bottom: 28px;

    .auth-title {
      font-size: 32px;
      font-weight: 800;
      margin: 0 0 8px 0;
      background: linear-gradient(135deg, $primary-orange, #ffa500);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;

      span {
        font-weight: 300;
        -webkit-text-fill-color: #1a1a1a;
      }
    }

    .auth-subtitle {
      font-size: 15px;
      color: #666;
      margin: 0;
    }
  }
}

.auth-tabs {
  display: flex;
  background: #f1f3f5;
  border-radius: 12px;
  padding: 4px;
  margin-bottom: 24px;

  .tab {
    flex: 1;
    padding: 10px;
    border: none;
    background: transparent;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    color: #666;
    cursor: pointer;
    transition: all 0.3s ease;

    &.active {
      background: #fff;
      color: $primary-orange;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }

    &:hover:not(.active) {
      color: #1a1a1a;
    }
  }
}

.auth-form {
  .form-group {
    margin-bottom: 18px;

    label {
      display: block;
      font-size: 13px;
      font-weight: 600;
      color: #333;
      margin-bottom: 6px;
    }

    .input-wrapper {
      position: relative;
      display: flex;
      align-items: center;

      svg {
        position: absolute;
        left: 14px;
        color: #999;
        pointer-events: none;
      }

      input,
      select {
        width: 100%;
        padding: 12px 12px 12px 44px;
        border: 2px solid #e9ecef;
        border-radius: 10px;
        font-size: 15px;
        transition: all 0.3s ease;
        background: #f8f9fa;
        color: #1a1a1a;

        &:focus {
          border-color: $primary-orange;
          background: #fff;
          box-shadow: 0 0 0 4px rgba(255, 128, 0, 0.1);
          outline: none;
        }

        &::placeholder {
          color: #adb5bd;
        }

        &.error {
          border-color: #ff4444;
        }
      }

      select {
        appearance: none;
        padding-right: 12px;
        cursor: pointer;

        option {
          padding: 8px;
        }
      }

      .toggle-password {
        position: absolute;
        right: 14px;
        background: none;
        border: none;
        padding: 4px;
        cursor: pointer;
        color: #999;

        &:hover {
          color: #333;
        }

        svg {
          position: static;
          pointer-events: none;
        }
      }
    }

    .password-hint {
      margin-top: 4px;
      font-size: 12px;
      color: #ff4444;
    }
  }

  .form-options {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 18px 0;

    @media (max-width: 480px) {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
    }

    .checkbox-label {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 14px;
      color: #555;
      cursor: pointer;
      position: relative;

      input[type="checkbox"] {
        position: absolute;
        opacity: 0;
        cursor: pointer;
        height: 0;
        width: 0;

        &:checked ~ .checkmark {
          background: $primary-orange;
          border-color: $primary-orange;

          &::after {
            content: "✓";
            color: #fff;
            font-size: 14px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
        }
      }

      .checkmark {
        width: 20px;
        height: 20px;
        border: 2px solid #ddd;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
        flex-shrink: 0;

        &::after {
          content: "";
        }
      }

      a {
        color: $primary-orange;
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }
    }

    .forgot-password {
      background: none;
      border: none;
      color: $primary-orange;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;

      &:hover {
        color: $hover-orange;
        text-decoration: underline;
      }
    }
  }

  .submit-btn {
    width: 100%;
    padding: 14px;
    background: $primary-orange;
    color: #fff;
    border: none;
    border-radius: 10px;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    &:hover:not(:disabled) {
      background: $hover-orange;
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(255, 128, 0, 0.3);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .error-message {
    margin-top: 12px;
    padding: 10px 14px;
    background: #fff0f0;
    color: #ff4444;
    border-radius: 8px;
    font-size: 14px;
    border: 1px solid #ffd4d4;
  }
}

.reset-password {
  margin-top: 16px;

  h3 {
    font-size: 20px;
    color: #1a1a1a;
    margin: 0 0 8px 0;
  }

  p {
    font-size: 14px;
    color: #666;
    margin: 0 0 16px 0;
  }

  .reset-actions {
    display: flex;
    gap: 12px;
    margin-top: 16px;

    .btn-secondary {
      flex: 1;
      padding: 12px;
      background: #f1f3f5;
      color: #333;
      border: none;
      border-radius: 10px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        background: #e9ecef;
      }
    }

    .submit-btn {
      flex: 2;
    }
  }

  .success-message {
    margin-top: 12px;
    padding: 10px 14px;
    background: #f0fff4;
    color: #22a45a;
    border-radius: 8px;
    font-size: 14px;
    border: 1px solid #b8e6c8;
  }
}

.social-auth {
  margin-top: 20px;

  .divider {
    display: flex;
    align-items: center;
    margin-bottom: 16px;

    &::before,
    &::after {
      content: "";
      flex: 1;
      height: 1px;
      background: #e9ecef;
    }

    span {
      padding: 0 16px;
      font-size: 13px;
      color: #999;
    }
  }

  .social-btn {
    width: 100%;
    padding: 12px;
    background: #fff;
    border: 2px solid #e9ecef;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 600;
    color: #333;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;

    &:hover {
      border-color: $primary-orange;
      background: #f8f9fa;
      transform: translateY(-2px);
    }

    svg {
      width: 20px;
      height: 20px;
    }
  }
}

.spinner {
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top: 3px solid #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Scrollbar styling */
.auth-modal::-webkit-scrollbar {
  width: 6px;
}

.auth-modal::-webkit-scrollbar-track {
  background: transparent;
}

.auth-modal::-webkit-scrollbar-thumb {
  background: #ddd;
  border-radius: 3px;
}

.auth-modal::-webkit-scrollbar-thumb:hover {
  background: #ccc;
}
</style>
